<script setup lang="ts">
import { computed, onUnmounted } from "vue";

import Tooltip from "~/components/dumb/Tooltip.vue";
import { DartboardKind } from "~/shared/enums";
import type { Dartboard } from "~/shared/types";
import { useDataStore } from "~/stores";
import { getPageDisplayName } from "~/utils/common";
import { makeRelativeTimeForDatesDateRef } from "~/utils/time";

const { dartboard } = defineProps<{
  dartboard: Dartboard;
}>();

const dataStore = useDataStore();

const pageName = computed(() => getPageDisplayName(dartboard, dataStore.getSpaceByDuid));
const sprintLabel = computed(() => `Sprint ${dartboard.index}`);

const startedAtPretty = makeRelativeTimeForDatesDateRef(
  computed(() => dartboard.startedAt ?? new Date().toISOString())
);
const finishedAtPretty = makeRelativeTimeForDatesDateRef(
  computed(() => dartboard.finishedAt ?? new Date().toISOString())
);

const plannedFinishAtPretty = makeRelativeTimeForDatesDateRef(
  computed(() => dartboard.plannedFinishAt ?? new Date().toISOString())
);

const startAtDateOrNull = computed(() =>
  dartboard.kind === DartboardKind.NEXT
    ? dataStore.getDartboardsBySpaceDuidOrdered(dartboard.spaceDuid).find((e) => e.kind === DartboardKind.ACTIVE)
        ?.plannedFinishAt
    : null
);

const plannedStartAtPretty = makeRelativeTimeForDatesDateRef(
  computed(() => startAtDateOrNull.value ?? new Date().toISOString())
);

onUnmounted(() => {
  startedAtPretty.value.destroy();
  finishedAtPretty.value.destroy();
});
</script>

<template>
  <div class="flex flex-col gap-0.5 py-2 pr-2 pl-3 text-xs text-md select-none">
    <p v-if="sprintLabel !== pageName">{{ sprintLabel }}</p>
    <p v-if="dartboard.startedAt" class="flex">
      <span class="text-vlt">Started&nbsp;</span>
      <Tooltip :text="startedAtPretty.full">
        <span>{{ startedAtPretty.short }}</span>
      </Tooltip>
    </p>
    <p v-else-if="startAtDateOrNull" class="flex">
      <span class="text-vlt">Planned to start&nbsp;</span>
      <Tooltip :text="plannedStartAtPretty.full">
        <span>{{ plannedStartAtPretty.short }}</span>
      </Tooltip>
    </p>
    <p v-if="dartboard.finishedAt" class="flex">
      <span class="text-vlt">Finished&nbsp;</span>
      <Tooltip :text="finishedAtPretty.full">
        <span>{{ finishedAtPretty.short }}</span>
      </Tooltip>
    </p>
    <p v-else-if="dartboard.plannedFinishAt" class="flex">
      <span class="text-vlt">Planned to finish&nbsp;</span>
      <Tooltip :text="plannedFinishAtPretty.full">
        <span>{{ plannedFinishAtPretty.short }}</span>
      </Tooltip>
    </p>
  </div>
</template>
